import { useRef, useState } from 'react'
import { getGraphqlErrorMessage } from './ai-chat-live-session'
import { useArchiveAiChatSessionMutation } from '@/graphql/mutations/archiveAiChatSessionMutation.gql.generated'
import { useResumeAiChatSessionMutation } from '@/graphql/mutations/resumeAiChatSessionMutation.gql.generated'

type ActionsInput = {
  activeSessionId?: string
  blocked: boolean
  setSelectedSessionId: (id: string | null) => void
  setTitle: (id: string, title: string) => void
  setError: (message: string | undefined) => void
  setHistoryOpen: (open: boolean) => void
  refetch: () => Promise<unknown>
}

export function useAiChatConversationActions(input: ActionsInput) {
  const [archiveSession] = useArchiveAiChatSessionMutation()
  const [resumeSession] = useResumeAiChatSessionMutation()
  const [transitioning, setTransitioning] = useState(false)
  const transitionRef = useRef(false)
  const transition = async (operation: () => Promise<void>) => {
    if (input.blocked || transitionRef.current) return
    transitionRef.current = true
    setTransitioning(true)
    input.setError(undefined)
    try {
      await operation()
    } catch (error) {
      input.setError(getGraphqlErrorMessage(error))
    } finally {
      transitionRef.current = false
      setTransitioning(false)
    }
  }
  const newConversation = () =>
    transition(async () => {
      if (input.activeSessionId) {
        const result = await archiveSession({
          variables: { sessionId: input.activeSessionId },
        })
        if (!result.data?.archiveAiChatSession.id)
          throw new Error('Conversation could not be archived.')
      }
      input.setSelectedSessionId(null)
      input.setHistoryOpen(false)
      await input.refetch()
    })
  const selectConversation = (sessionId: string) =>
    transition(async () => {
      if (sessionId !== input.activeSessionId) {
        const result = await resumeSession({ variables: { sessionId } })
        const session = result.data?.resumeAiChatSession
        if (!session) throw new Error('Conversation could not be resumed.')
        input.setTitle(session.id, session.title)
        input.setSelectedSessionId(session.id)
      }
      input.setHistoryOpen(false)
      await input.refetch()
    })

  return { transitioning, newConversation, selectConversation }
}
